import { defineStore } from 'pinia'
import type { ItemType } from '~/types/lineitem'
import type {
  CreateWishlistPayload,
  FavoritedItem,
  SummaryWishlist,
  UpdateWishlistPayload,
  Wishlist,
  WishlistItem
} from '~/types/wishlist'
import { NotificationType } from '~/types/notifications'
import { useNotificationsStore } from '~/stores/notifications'
import { useNavigationStore } from '~/stores/navigation'
import { pushAddToWishlist } from '~/utils/gtm'
import { normalizeSlug } from '~/utils/shop'
import { SHOP_ROOT } from '~/utils/categoryRoutes'
enum URL {
  INDEX = 'wishlist',
  SUMMARY = 'wishlist/summary',
  RENAME = 'wishlist/rename',
  LIST_INDEX = 'wishlist/lists',
  ITEMS = 'wishlist/items/all',
  ITEMS_ANY = 'wishlist/items/any',
  MOVE_ITEM = 'wishlist/move'
}

type WishlistStoreItem = FavoritedItem | WishlistItem

export const useWishlistStore = defineStore('wishlist', () => {
  const route = useRoute()
  const router = useRouter()

  const allLists = ref<Wishlist[]>([])
  const summaryLists = ref<SummaryWishlist[]>([])
  const items = ref<WishlistStoreItem[]>([])
  const listsWithItem = ref<string[]>([])
  const quickAddItem = async (payload: { productId: string, quantity: number, itemType?: ItemType, wishlistId: string }) => {
    const response = await $api()<Wishlist>(`wishlist/${payload.wishlistId}/${payload.productId}/${payload.quantity}`, { query: { ...route.query }, method: 'post' })
    updateListState(response)
    // todo release FavoritedItem vs WishlistItem unify/check.
    items.value = response.items

    const item = response.items.find((item: WishlistItem) => item.productId === payload.productId) as WishlistItem
    // Add to GTM being done from wishlist page so category is not needed
    const {proxy:gtm} = useScriptGoogleTagManager()
    pushAddToWishlist(gtm, item)

    return item
  }
  const fetchLists = async () => {
    const url = `${URL.LIST_INDEX}`
    const response = await $api()<{ wishlists: Wishlist[] }>(url)
    allLists.value = response.wishlists
  }
  const fetchAllItems = async () => {
    const url = `${URL.ITEMS}`
    const response = await $api()<FavoritedItem[]>(url)
    items.value = response
  }
  const fetchSummary = async () => {
    const url = `${URL.SUMMARY}`
    const response = await $api()<{ wishlists: SummaryWishlist[] }>(url)
    summaryLists.value = response.wishlists
  }

  const updateListState = (wishlist: Wishlist) => {
    allLists.value = [wishlist, ...allLists.value.filter(x => x.id !== wishlist.id)]
    summaryLists.value = [wishlist, ...summaryLists.value.filter(x => x.id !== wishlist.id)]
  }
  const deleteList = async (wishlistId: string) => {
    const url = `${URL.INDEX}/${wishlistId}`
    await $api()(url, { method: 'DELETE' })
    allLists.value = allLists.value.filter(x => x.id !== wishlistId)
    summaryLists.value = summaryLists.value.filter(x => x.id !== wishlistId)
    await fetchLists()
  }
  const createList = async (createWishlistPayload: CreateWishlistPayload) => {
    const url = `${URL.INDEX}`
    const response = await $api()<Wishlist>(url, { method: 'POST', body: createWishlistPayload })
    updateListState(response)
  }

  const updateWishlistWithQuantity = async ({ wishlistId, itemId, quantity, page, pageSize, orderBy }: {wishlistId: string, itemId: string, quantity: number, page: string, pageSize: string, orderBy: string }) => {
    const url = `${URL.INDEX}/${wishlistId}/${itemId}/${quantity}`
    const response = await $api()<Wishlist>(url, { method: 'PUT', query: { page, pageSize, orderBy } })
    updateListState(response)
  }

  const renameList = async (updateWishlistPayload: UpdateWishlistPayload) => {
    const url = `${URL.RENAME}`
    const response = await $api()<Wishlist>(url, { method: 'POST', body: updateWishlistPayload })
    updateListState(response)
  }

  const fetchList = async ({ listId, page, pageSize, orderBy }: { listId: string, page: string, pageSize: string, orderBy: string }) => {
    const url = `${URL.INDEX}/${listId}`
    const response = await $api()<Wishlist>(url, { query: { page, pageSize, orderBy } })
    updateListState(response)
    items.value = response.items
  }

  const fetchListsWithItem = async (itemId: string) => {
    const url = `${URL.ITEMS_ANY}/${itemId}`
    const { ids } = await $api()<{ ids: string[] }>(url)
    listsWithItem.value = ids
  }
  const addItemToWishlist = async ({ wishlistId, itemId, quantity, text }: {wishlistId: string | undefined, itemId: string, quantity: number, text: string}) => {
    const url = `wishlist/${wishlistId}/${itemId}/${quantity}`
    const response = await $api()<Wishlist>(url, { method: 'PUT' })
    updateListState(response)
    await fetchAllItems()

    // Add to GTM use page to determine category
    const route = router.currentRoute.value
    const navigationStore = useNavigationStore()
    const slugParam = route.params.slug
    const slugParts = Array.isArray(slugParam) ? slugParam : slugParam ? [String(slugParam)] : []
    const category = route.name === 'shop-products-slug'
      ? navigationStore.categoryBySlug(normalizeSlug([SHOP_ROOT, ...slugParts]))?.name
      : undefined
    const item = response.items.find((item: WishlistItem) => item.productId === itemId) as WishlistItem

    const {proxy:gtm} = useScriptGoogleTagManager()
    pushAddToWishlist(gtm, item, category)

    const notificationStore = useNotificationsStore()
    const currentRouteName = router.currentRoute.value.name
    const badRoute = notificationStore.isRouteBlacklistedForNotifications(currentRouteName)

    if (!badRoute) {
      notificationStore.showNotification({
        notification: {
          type: NotificationType.WishlistItemAdded,
          props: {
            wishlistId,
            text
          }
        }
      })
    }
  }

  const removeItemFromWishlist = async ({ wishlistId, itemId, page, pageSize, orderBy }: {wishlistId: string | undefined, itemId: string, page?: string, pageSize?: string, orderBy?: string }) => {
    const url = `wishlist/${wishlistId}/${itemId}`
    const response = await $api()<Wishlist>(url, { method: 'DELETE', query: { page, pageSize, orderBy } })
    updateListState(response)
    await fetchAllItems()
  }

  const itemFavorited = (id: string) => {
    return items.value.some(item => 'itemId' in item ? item.itemId === id : item.productId === id)
  }
  const hasItems = computed(() => items.value.length > 0)
  const listById = (id: string) => allLists.value.find(x => x.id === id)

  return {
    items,
    allLists,
    hasItems,
    listsWithItem,
    summaryLists,
    updateListState,
    listById,
    itemFavorited,
    addItemToWishlist,
    removeItemFromWishlist,
    quickAddItem,
    fetchLists,
    updateWishlistWithQuantity,
    deleteList,
    renameList,
    fetchList,
    fetchSummary,
    createList,
    fetchAllItems,
    fetchListsWithItem
  }
})
